<template>
	<div class="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
		<div class="max-w-7xl mx-auto">
			<div class="text-center mb-10">
				<h1 class="text-4xl font-bold text-gray-900">
					Quotation Details
				</h1>
				<p class="mt-2 text-lg text-gray-600">
					Reference:
					<span class="font-mono font-semibold">{{
						$route.params.id
					}}</span>
				</p>
			</div>

			<!-- Loading State -->
			<div v-if="!quotationData" class="space-y-6">
				<div
					v-for="n in 8"
					:key="n"
					class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse"
				>
					<div class="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
					<div class="space-y-3">
						<div class="h-4 bg-gray-200 rounded w-full"></div>
						<div class="h-4 bg-gray-200 rounded w-4/5"></div>
						<div class="h-4 bg-gray-200 rounded w-3/5"></div>
					</div>
				</div>
			</div>

			<!-- Quotation Content -->
			<div v-else class="space-y-8">
				<!-- Header Card -->
				<div
					class="bg-blue-800 text-white rounded-2xl shadow-lg p-8 text-center"
				>
					<h2 class="text-2xl font-bold">
						Quotation #{{ quotationData.systemQuotationRef }}
					</h2>
					<div class="mt-4 flex justify-center items-center gap-3">
						<span
							class="px-4 py-2 bg-white/20 backdrop-blur rounded-full text-sm font-semibold"
						>
							{{ quotationData.quotationStatus || 'Draft' }}
						</span>
						<span class="text-sm opacity-90">{{
							quotationData.bussinessType
						}}</span>
					</div>
				</div>

				<div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
					<!-- Client Details -->
					<div
						class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition"
					>
						<h3
							class="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3"
						>
							<svg
								class="w-6 h-6 text-blue-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
								/>
							</svg>
							Client Details
						</h3>
						<dl class="space-y-4 text-sm">
							<div class="flex justify-between">
								<dt class="text-gray-600">Name</dt>
								<dd class="font-semibold text-gray-900">
									{{
										quotationData.clientDetails.names
											.firstName
									}}
									{{
										quotationData.clientDetails.names
											.lastName
									}}
								</dd>
							</div>
							<div class="flex justify-between">
								<dt class="text-gray-600">Email</dt>
								<dd class="font-medium text-blue-700">
									{{
										quotationData.clientDetails.email || '—'
									}}
								</dd>
							</div>
							<div class="flex justify-between">
								<dt class="text-gray-600">KRA PIN</dt>
								<dd class="font-mono font-semibold">
									{{
										quotationData.clientDetails.kraPin ||
										'—'
									}}
								</dd>
							</div>
						</dl>
					</div>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-8">
						<div
							class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
						>
							<h3 class="text-xl font-bold text-gray-900 mb-5">
								Underwriter
							</h3>
							<div class="flex justify-between">
								<dt class="text-gray-600">Name</dt>
								<dd class="font-semibold text-gray-900">
									{{ quotationData.underwriter_details.name }}
								</dd>
							</div>
						</div>

						<!-- Product Info -->
						<div
							class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition"
						>
							<h3
								class="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3"
							>
								<svg
									class="w-6 h-6 text-green-600"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
									/>
								</svg>
								Product Information
							</h3>
							<dl class="space-y-4 text-sm">
								<div class="flex justify-between">
									<dt class="text-gray-600">Product</dt>
									<dd class="font-semibold text-gray-900">
										{{
											quotationData.product_details
												.productName
										}}
									</dd>
								</div>
								<div class="flex justify-between">
									<dt class="text-gray-600">Class</dt>
									<dd class="font-medium">
										{{
											quotationData.product_details
												.insuranceClass
										}}
									</dd>
								</div>
								<div class="flex justify-between">
									<dt class="text-gray-600">Base Rate</dt>
									<dd class="font-bold text-green-700">
										{{
											quotationData.product_details
												.basePremiumRate.value
										}}
									</dd>
								</div>
							</dl>
						</div>
					</div>

					<!-- Cover Period -->
					<div
						class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
					>
						<h3
							class="text-xl font-bold text-gray-900 mb-5 flex items-center gap-3"
						>
							<svg
								class="w-6 h-6 text-purple-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
								/>
							</svg>
							Cover Period
						</h3>
						<div
							class="grid grid-cols-2 md:grid-cols-4 gap-6 text-center"
						>
							<div class="bg-purple-50 rounded-lg p-4">
								<p class="text-xs text-purple-700 font-medium">
									Start Date
								</p>
								<p class="text-sm font-bold text-purple-900">
									{{
										formatDate(
											quotationData.quotationCoverDetails
												.startDate,
										)
									}}
								</p>
							</div>
							<div class="bg-purple-50 rounded-lg p-4">
								<p class="text-xs text-purple-700 font-medium">
									End Date
								</p>
								<p class="text-sm font-bold text-purple-900">
									{{
										formatDate(
											quotationData.quotationCoverDetails
												.endDate,
										)
									}}
								</p>
							</div>
							<div class="bg-purple-50 rounded-lg p-4">
								<p class="text-xs text-purple-700 font-medium">
									Type
								</p>
								<p class="text-sm font-bold text-purple-900">
									{{
										quotationData.quotationCoverDetails
											.coverType
									}}
								</p>
							</div>
							<div class="bg-purple-50 rounded-lg p-4">
								<p class="text-xs text-purple-700 font-medium">
									Duration
								</p>
								<p
									class="text-2xl font-extrabold text-purple-900"
								>
									{{
										quotationData.quotationCoverDetails
											.coverPeriod
									}}
									<span class="text-sm">days</span>
								</p>
							</div>
						</div>
					</div>

					<!-- Risk Details -->
					<div
						v-if="quotationData.risks.length"
						class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
					>
						<h3 class="text-xl font-bold text-gray-900 mb-5">
							Insured Risks
						</h3>
						<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div
								v-for="risk in quotationData.risks"
								:key="risk.riskRef"
								class="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition"
							>
								<div class="flex justify-between items-start">
									<div>
										<p class="font-semibold text-gray-900">
											{{ risk.riskType }}
										</p>
										<p class="text-sm text-gray-600 mt-1">
											Ref: {{ risk.riskRef }}
										</p>
									</div>
									<p class="text-lg font-bold text-blue-700">
										{{ formatKES(risk.value) }}
									</p>
								</div>
							</div>
						</div>
					</div>
					<div
						v-if="quotationData.quotationchargesApplied.length"
						class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
					>
						<h3 class="text-xl font-bold text-gray-900 mb-5">
							Applied Charges
						</h3>
						<div class="space-y-3">
							<div
								v-for="c in quotationData.quotationchargesApplied"
								:key="c.charge_code"
								class="flex justify-between items-center py-3 px-4 bg-gray-50 rounded-lg"
							>
								<span class="font-medium text-gray-700">{{
									c.charge_name
								}}</span>
								<span class="font-bold text-gray-900">{{
									formatKES(c.value)
								}}</span>
							</div>
						</div>
					</div>
					<!-- Premium Breakdown -->
					<div
						class="bg-primary text-white rounded-2xl shadow-lg p-8"
					>
						<h3 class="text-2xl font-bold mb-6 text-center">
							Premium Summary
						</h3>
						<div
							class="grid grid-cols-1 md:grid-cols-2 gap-6 text-lg"
						>
							<div>
								<p class="opacity-90">Base Premium</p>
								<p class="text-2xl font-bold">
									{{
										formatKES(
											quotationData.quotationPremium
												.basePremium,
										)
									}}
								</p>
							</div>
							<div>
								<p class="opacity-90">Additional Premium</p>
								<p class="text-2xl font-bold">
									{{
										formatKES(
											quotationData.quotationPremium
												.additionalPremium,
										)
									}}
								</p>
							</div>
							<div>
								<p class="opacity-90">Total Charges & Levies</p>
								<p class="text-2xl font-bold">
									{{
										formatKES(
											quotationData.quotationPremium
												.totalCharges,
										)
									}}
								</p>
							</div>
							<div
								class="md:col-span-2 text-center border-t border-white/30 pt-6 mt-4"
							>
								<p class="text-xl opacity-90">
									Total Payable Premium
								</p>
								<p class="text-4xl font-extrabold mt-2">
									{{
										formatKES(
											quotationData.quotationPremium.totalPremium.toFixed(),
										)
									}}
								</p>
							</div>
						</div>
					</div>
					<div class="p-6 sm:p-8 lg:p-12 bg-white">
						<div class="flex items-center mb-6 sm:mb-8">
							<div
								class="w-8 h-8 sm:w-10 sm:h-10 bg-green-600 rounded-lg flex items-center justify-center mr-3"
							>
								<svg
									class="w-4 h-4 sm:w-5 sm:h-5 text-white"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
									/>
								</svg>
							</div>
							<h2
								class="text-xl sm:text-2xl font-bold text-gray-900"
							>
								M-Pesa Payment
							</h2>
						</div>

						<!-- M-Pesa Number Input -->
						<div class="mb-6 sm:mb-8">
							<label
								for="mpesaNumber"
								class="block text-sm font-semibold text-gray-700 mb-3"
							>
								M-Pesa Phone Number
							</label>
							<div class="relative">
								<div
									class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
								>
									<svg
										class="h-4 w-4 sm:h-5 sm:w-5 text-gray-400"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
										/>
									</svg>
									<span
										class="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500 font-medium"
										>+254</span
									>
								</div>
								<input
									type="tel"
									id="mpesaNumber"
									v-model="phoneInput"
									@input="formatPhoneNumber"
									placeholder=""
									maxlength="9"
									required
									class="block w-full pl-16 sm:pl-20 pr-4 py-3 sm:py-4 text-base sm:text-lg border-2 border-gray-200 rounded-lg sm:rounded-xl shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 outline-none"
								/>
							</div>
							<p
								v-if="phoneError"
								class="mt-2 text-xs sm:text-sm text-red-600 flex items-center"
							>
								<svg
									class="w-3 h-3 sm:w-4 sm:h-4 mr-1"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								{{ phoneError }}
							</p>
						</div>
						<div class="flex my-3 gap-3 items-center">
							<input
								v-model="isTermsChecked"
								type="checkbox"
								@change="handleChecked"
								class="h-4 w-4"
							/>
							<span
								>I accept the
								<a
									class="font-semibold underline text-blue-700"
									target="_blank"
									href="/terms"
									>Terms, Privacy Policy</a
								></span
							>
						</div>
						<!-- Payment Status -->
						<div class="mb-6 sm:mb-8">
							<div
								v-if="paymentStatus === 'pending'"
								class="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4"
							>
								<div class="flex items-center">
									<svg
										class="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>

									<p
										class="text-xs sm:text-sm text-blue-800 font-medium"
									>
										Enter your M-Pesa number to receive
										payment prompt
									</p>
								</div>
							</div>

							<div
								v-else-if="paymentStatus === 'processing'"
								class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4"
							>
								<div class="flex items-center mb-1 sm:mb-2">
									<div
										class="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-yellow-600 mr-2"
									></div>
									<p
										class="text-xs sm:text-sm text-yellow-800 font-medium"
									>
										Check your phone for the M-Pesa prompt
									</p>
								</div>
								<p
									class="text-yellow-700 text-xs sm:text-sm ml-6 sm:ml-7"
								>
									Waiting for your confirmation...
								</p>
							</div>

							<div
								v-else-if="paymentStatus === 'failed'"
								class="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4"
							>
								<div class="flex items-center">
									<svg
										class="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
									<p
										class="text-xs sm:text-sm text-red-800 font-medium"
									>
										Payment Failed. Please try again.
									</p>
								</div>
							</div>

							<div
								v-else-if="paymentStatus === 'success'"
								class="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4"
							>
								<div class="flex items-center">
									<svg
										class="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M5 13l4 4L19 7"
										/>
									</svg>
									<p
										class="text-xs sm:text-sm text-green-800 font-medium"
									>
										Payment Successful! Your policy is being
										processed.
									</p>
								</div>
							</div>
						</div>

						<!-- Action Buttons -->
						<div class="flex flex-col gap-3 sm:gap-4">
							<!-- Main action buttons row -->
							<div
								class="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 sm:gap-4"
							>
								<button
									@click="initiatePayment"
									:disabled="
										paymentStatus === 'processing' ||
										!isValidPhoneNumber ||
										!isTermsChecked
									"
									:class="[
										'flex items-center justify-center w-full px-6 py-3 text-sm sm:text-base text-white font-semibold rounded-lg transition-all duration-200',
										isTermsChecked &&
										isValidPhoneNumber &&
										paymentStatus === 'pending'
											? 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
											: 'bg-gray-400 cursor-not-allowed',
									]"
								>
									<svg
										v-if="paymentStatus === 'processing'"
										class="animate-spin -ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5 text-white"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											class="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											stroke-width="4"
										></circle>
										<path
											class="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									<svg
										v-else
										class="w-4 h-4 sm:w-5 sm:h-5 mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
										/>
									</svg>
									{{
										paymentStatus === 'processing'
											? 'Processing...'
											: 'Pay Now'
									}}
								</button>
							</div>

							<!-- Pay Later button - separate row for better spacing -->
						</div>

						<!-- Security Note -->
						<div
							class="mt-6 sm:mt-8 bg-gray-50 rounded-lg p-3 sm:p-4"
						>
							<div class="flex items-start">
								<svg
									class="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-0.5 flex-shrink-0"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
									/>
								</svg>
								<div>
									<p
										class="text-xs sm:text-sm font-medium text-gray-700 mb-1"
									>
										Secure Payment
									</p>
									<p class="text-xs text-gray-600">
										Your payment is protected by M-Pesa's
										secure payment system. Never share your
										M-Pesa PIN with anyone.
									</p>
								</div>
							</div>
						</div>
					</div>

					<!-- Applied Charges -->

					<!-- Underwriter & Payment -->

					<!-- <div class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-5">Payment Status</h3>
            <div class="text-center py-6">
              <div class="inline-flex items-center gap-3 px-6 py-3 rounded-full text-lg font-bold"
                :class="quotationData.quotationPayment.paymentStatus === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'">
                {{ quotationData.quotationPayment.paymentStatus || 'Pending' }}
              </div>
              <p class="mt-4 text-2xl font-bold text-gray-900">
                {{ formatKES(quotationData.quotationPayment.paidAmount) || '—' }}
              </p>
              <p class="text-sm text-gray-600 mt-2">via {{ quotationData.quotationPayment.paymentChannel || 'N/A' }}</p>
            </div>
          </div> -->
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import {
	CheckPaymentDetails,
	CompleteQuotation,
	GetDirectQuotationDetails,
	RequestPayment,
} from '../../api/insurance';

export default {
	name: 'DirectQuotation',
	data() {
		return {
			quotationData: null,
			phoneInput: '',
			phoneError: '',
			isValidPhoneNumber: false,
			isTermsChecked: false,
		};
	},
	computed: {
		...mapState('insurance/motorPrivate', ['paymentStatus']),
		...mapGetters('quotation', [
			'selectCalculatedQuote',
			'selectQuotationData',
			'selectSystemQuotation',
			'selectInvoiceRef',
		]),
	},

	methods: {
		...mapMutations('insurance/motorPrivate', ['SET_PAYMENT_STATUS']),
		...mapActions('quotation', ['setPaymentResult']),
		...mapActions('quotation', ['setSystemQuotationRef', 'setInvoiceRef']),
		formatPhoneNumber() {
			this.phoneInput = this.phoneInput
				.replace(/^\+?254/, '')
				.replace(/\D/g, '');
			if (this.phoneInput.length === 9) {
				if (/^(7|1)\d{8}$/.test(this.phoneInput)) {
					this.phoneError = '';
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Valid Phone Number',
						message: 'Your phone number is valid.',
						duration: 3000,
					});
					this.isValidPhoneNumber = true;
				} else {
					this.phoneError =
						'Please enter a valid Safaricom number starting with 7 or 1';
					Event.$emit('notifications:show', {
						type: 'error',
						title: 'Invalid Phone Number',
						message: this.phoneError,
						duration: 3000,
					});
					this.isValidPhoneNumber = false;
				}
			} else {
				this.phoneError =
					this.phoneInput.length > 0
						? 'Phone number must be 9 digits'
						: '';
				this.isValidPhoneNumber = false;
			}
		},
		handleChecked() {
			if (this.isTermsChecked) {
				console.log('tnc accepted');
			}
		},
		async CompletedDirectQuotation() {
			try {
				const response = await CompleteQuotation({
					systemQuotationRef: this.$route.params.id,
				});
				if (response.status) {
					this.setInvoiceRef(response.data.invoiceSystemRef);
					console.log('invoiceref', response.data.invoiceSystemRef);
					this.$emit('summary-confirmed', {
						selectedBenefits: this.coreBenefits,
						totalPremium: this.calculatedPremium,
					});
					// this.$router.push({ name: 'Payment' });
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Quotation Completed',
						message:
							'Your quotation has been successfully completed.',
						duration: 3000,
					});
				} else {
					throw new Error('Failed to complete quotation.');
				}
			} catch (error) {}
		},
		paymentData() {
			return {
				invoiceSystemRef:
					this.selectInvoiceRef ||
					this.quotationData.quotationPayment.invoiceSystemRef,
				invoicePaymentDetails: {
					paymentProvider: 'mpesa',
					params: {
						PhoneNumber: '254' + this.phoneInput,
					},
				},
			};
		},

		async pollPaymentStatus(paymentRef, retries = 6, interval = 5000) {
			for (let i = 0; i < retries; i++) {
				await new Promise((resolve) => setTimeout(resolve, interval));

				const response = await CheckPaymentDetails({
					paymentSystemRef: paymentRef,
				});
				console.log(`Poll #${i + 1}:`, response.data);

				if (
					response.data.paymentRequestStatus === 'success' ||
					response.data.paymentRequestStatus === 'failed'
				) {
					return response;
				}
			}

			return {
				status: false,
				data: {
					paymentRequestStatus: 'failed',
					statusMessage: 'Payment timeout',
				},
			};
		},

		async initiatePayment() {
			const payload = this.paymentData();
			console.log('Initiating payment with data:', payload);

			if (!this.isValidPhoneNumber) {
				this.phoneError = 'Invalid phone number';
				return;
			}

			this.SET_PAYMENT_STATUS('processing');

			try {
				const res = await RequestPayment(payload);
				console.log('Payment response:', res);

				if (!res || !res.status || !res.data.paymentRef) {
					throw new Error('Invalid API response');
				}

				const paymentRef = res.data.paymentRef;

				const response = await this.pollPaymentStatus(paymentRef);

				if (response && response.status && response.data) {
					const { paymentRequestStatus, statusMessage } =
						response.data;

					if (paymentRequestStatus === 'success') {
						this.SET_PAYMENT_STATUS('success');
						this.setPaymentResult(response.data);

						Event.$emit('notifications:show', {
							type: 'success',
							title: 'Payment Successful',
							message:
								'Your payment has been successfully processed. Login into your account to view your policy details.',
							duration: 3000,
						});

						this.$router.push('/newportal');
					} else {
						this.SET_PAYMENT_STATUS('failed');
						Event.$emit('notifications:show', {
							type: 'error',
							title: 'Payment Failed',
							message: statusMessage || 'No payment received',
							duration: 6000,
						});
					}
				}
			} catch (error) {
				console.error('Payment error:', error);

				this.SET_PAYMENT_STATUS('failed');
				this.phoneError = 'Payment failed. Please try again.';

				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Payment Error',
					message:
						error.response.data.errors ||
						error.message ||
						'An unexpected error occurred',
					duration: 5000,
				});

				setTimeout(() => {
					this.SET_PAYMENT_STATUS('pending');
				}, 3000);
			} finally {
				this.SET_PAYMENT_STATUS('pending');
			}
		},
		async fetchQuotationsReview() {
			try {
				const res = await GetDirectQuotationDetails({
					systemQuotationRef: this.$route.params.id,
				});

				if (res.status && res.data[0]) {
					this.quotationData = res.data[0];
				}
			} catch (error) {
				console.error('Failed to fetch quotation:', error);
			}
		},

		formatDate(date) {
			if (!date) return '—';
			return new Date(date).toLocaleDateString('en-GB', {
				day: 'numeric',
				month: 'long',
				year: 'numeric',
			});
		},

		formatKES(amount) {
			if (!amount) return 'KES 0';
			return new Intl.NumberFormat('en-KE', {
				style: 'currency',
				currency: 'KES',
				minimumFractionDigits: 0,
			}).format(amount);
		},
		resetPaymentState() {
			this.SET_PAYMENT_STATUS('pending');
			this.phoneInput = '';
			this.phoneError = '';
			this.isValidPhoneNumber = false;
			console.log('Reset paymentStatus to pending');
		},
	},

	mounted() {
		this.resetPaymentState();
		console.log(this.phoneInput);
		this.fetchQuotationsReview();
		this.CompletedDirectQuotation();
	},
};
</script>
